import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export type SidebarTreeRowProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  padLeft: number
  /** Focused leaf (fill + left bar). */
  active?: boolean
  /** Ancestor / section owns focused path (text only). Ignored when active. */
  contains?: boolean
  /** Open in working set but not focused (light wash). Ignored when active. */
  open?: boolean
  icon: React.ReactNode
  label: React.ReactNode
  subtitle?: React.ReactNode
  badge?: React.ReactNode
  chevron?: {
    expanded: boolean
    onClick: (e: React.MouseEvent) => void
  }
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void
  'data-testid'?: string
  role?: React.AriaRole
  'aria-expanded'?: boolean
  'aria-level'?: number
  tabIndex?: number
}

/** Tree row chrome (ws-nav-item SCSS). Moved from WsNavBrowseRow. */
export function SidebarTreeRow({
  padLeft,
  active = false,
  contains = false,
  open = false,
  icon,
  label,
  subtitle,
  badge,
  chevron,
  onContextMenu,
  className,
  style,
  'data-testid': testId,
  role = 'treeitem',
  'aria-expanded': ariaExpanded,
  'aria-level': ariaLevel,
  tabIndex,
  ...rest
}: SidebarTreeRowProps) {
  return (
    <div
      {...rest}
      role={role}
      aria-expanded={ariaExpanded}
      aria-level={ariaLevel}
      tabIndex={tabIndex}
      data-testid={testId}
      data-active={active ? 'true' : undefined}
      data-contains={contains && !active ? 'true' : undefined}
      data-open={open && !active ? 'true' : undefined}
      onContextMenu={onContextMenu}
      className={cn(
        'ws-nav-item ws-nav-row pr-2 py-1 text-sm select-none',
        active
          ? 'ws-nav-item--active text-sidebar-foreground'
          : open
            ? 'ws-nav-item--open'
            : contains
              ? 'ws-nav-item--contains'
              : 'text-sidebar-foreground/70',
        className,
      )}
      style={
        {
          ...style,
          '--ws-nav-pad-left': `${padLeft}px`,
        } as React.CSSProperties
      }
    >
      {chevron ? (
        <span
          role="presentation"
          className="ws-nav-row__leading"
          onClick={(e) => {
            e.stopPropagation()
            chevron.onClick(e)
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ChevronDown
            className="w-3 h-3 transition-transform duration-200 ease-out"
            style={{ transform: chevron.expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          />
        </span>
      ) : null}
      <span className="ws-nav-row__icon">{icon}</span>
      <span className="ws-nav-row__label flex min-w-0 flex-1 items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {subtitle ? (
          <span className="ws-nav-row__subtitle shrink-0 text-[10px] text-muted-foreground">{subtitle}</span>
        ) : null}
        {badge ? (
          <span className="shrink-0 rounded bg-muted px-1 py-0 text-[10px] text-muted-foreground">
            {badge}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/** @deprecated Use SidebarTreeRow - kept for browse import compatibility */
export const WsNavBrowseRow = SidebarTreeRow
